// StageInterviewTest — Enhancement #1 candidate journey page.
//
// One generic page that runs ANY configured-pipeline stage. The link carries
// the stageId; we resolve it (engine round, label, modality, brief) from the
// public stage endpoint, then render the matching engine:
//   round === "coding"  → CodingInterviewStage (live editor + voice)
//   otherwise           → AgentInterviewStage  (conversational voice)
//
// Route: /stage-interview/:jobId/:candidateId/:stageId

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import AgentInterviewStage from "../audio/AgentInterviewStage";
import CodingInterviewStage from "../coding/CodingInterviewStage";
import StageExamPage from "./StageExamPage";

export default function StageInterviewTest() {
  const { jobId, candidateId, stageId } = useParams();
  const navigate = useNavigate();
  const [stage, setStage]     = useState(null);
  const [jobTitle, setJobTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [done, setDone]       = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/public/jobs/${jobId}/stage/${stageId}`);
        if (r.data?.success) {
          setStage(r.data.data);
          setJobTitle(r.data.data.jobTitle || "");
        } else {
          setError(r.data?.message || "Could not load this assessment stage.");
        }
      } catch (e) {
        setError(e.response?.data?.message || "Could not load this assessment stage.");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId, stageId]);

  const handleComplete = (result) => {
    setDone(true);
    setTimeout(() => navigate("/test-complete", {
      state: {
        assessment: { overallScore: result?.overallScore, feedback: result?.recommendation },
        type: stage?.round || "stage",
        stageLabel: stage?.label || null,
        isExam: stage?.mode === "exam",
      },
    }), 1800);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-blue-100">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="w-20 h-20 rounded-full border-4 border-gray-200"></div>
            <div className="absolute top-0 left-0 w-20 h-20 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 text-lg">Loading your assessment…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-rose-100">
        <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full mx-4 text-center">
          <div className="text-rose-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Assessment unavailable</h2>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const isCoding = stage?.round === "coding";
  const isExam   = stage?.mode === "exam";

  // Assessment-type stages (aptitude, technical, case_study, etc.) require uploaded
  // material to run as an exam. If no material is ready yet, show a waiting screen
  // instead of falling back to the audio interview UI.
  const EXAM_ONLY_TYPES = new Set(["aptitude", "technical", "case_study", "custom", "communication"]);
  const isAwaitingMaterial = !isExam && EXAM_ONLY_TYPES.has(stage?.type);

  if (!done && isAwaitingMaterial) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-white to-blue-50">
        <div className="bg-white rounded-2xl shadow-md max-w-md w-full mx-4 p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Test Not Started Yet</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-4">
            The <span className="font-semibold text-gray-700">{stage?.label || "assessment"}</span> has not been activated by the hiring team yet.
            Please check back later or contact the recruiter for the scheduled time.
          </p>
          <div className="bg-blue-50 rounded-xl px-4 py-3 text-xs text-blue-600 font-medium">
            ⏳ Waiting for the test to be made available
          </div>
        </div>
      </div>
    );
  }

  // Exam mode: material was uploaded — render full proctored exam (no interview)
  if (!done && isExam) {
    return (
      <StageExamPage
        jobId={jobId}
        candidateId={candidateId}
        stageId={stageId}
        jobTitle={jobTitle}
        stageLabel={stage?.label || "Exam"}
        onComplete={handleComplete}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-blue-100">
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{jobTitle || "Assessment"}</h1>
            <p className="text-gray-600">{stage?.label || "Assessment Stage"}</p>
          </div>
          {stage?.type && (
            <span className="px-3 py-1 text-sm rounded-full bg-blue-50 text-blue-700 capitalize">
              {String(stage.type).replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 w-full">
        {!done && isCoding && (
          <CodingInterviewStage
            jobId={jobId}
            resumeId={candidateId}
            stageId={stageId}
            candidateName={jobTitle || "there"}
            onComplete={handleComplete}
          />
        )}
        {!done && !isCoding && (
          <div className="max-w-4xl mx-auto w-full p-6">
            <AgentInterviewStage
              round={stage?.round || "finalInterview"}
              jobId={jobId}
              resumeId={candidateId}
              stageId={stageId}
              candidateName={jobTitle || "there"}
              onComplete={handleComplete}
            />
          </div>
        )}
        {done && (
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full mx-4 text-center">
              <div className="text-green-500 text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-semibold text-green-600 mb-2">Assessment complete</h2>
              <p className="text-gray-600 text-sm">Thank you — we'll review your responses and be in touch.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
